import { useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { AlertBanner, Button } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useStorage } from "app/custom-hooks/storage/useStorage";
import { useMount } from "app/custom-hooks/useMount";
import extensionManagementTrackingService from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";

const TEAM_DEFAULTS_NOTE_SEEN_STORAGE_KEY = "si:management-extension-team-defaults-note-seen";

const ExtensionTeamDefaultsNoteExplainer = () => {
    const { translate } = useTranslate();

    return (
        <Stack
            data-automation="sales-intelligence-extension-team-defaults-note"
            gap="4px"
        >
            <Text
                as="strong"
                fontVariant="subtitle1"
            >
                {translate("si.management.extension.note.title")}
            </Text>
            <Text
                as="p"
                fontVariant="body1"
            >
                {translate("si.management.extension.note.body")}
            </Text>
        </Stack>
    );
};

export const ExtensionManagementBanner = () => {
    const { translate } = useTranslate();

    const [hasSeenNoteBefore, setHasSeenNoteBefore] = useStorage<boolean>({
        item: {
            key: TEAM_DEFAULTS_NOTE_SEEN_STORAGE_KEY,
            initialValue: false,
        },
        storage: window.localStorage,
    });

    const hasSeenNoteOnMount = useRef(hasSeenNoteBefore);
    const [noteExpanded, setNoteExpanded] = useState(false);

    useMount(() => {
        if (!hasSeenNoteBefore) {
            setHasSeenNoteBefore(true);
        }
    });

    return (
        <AlertBanner data-automation="sales-intelligence-extension-manage-alert-banner">
            <AlertBanner.Image iconName="info" />
            <AlertBanner.Description>
                {hasSeenNoteOnMount.current ? (
                    noteExpanded ? (
                        <ExtensionTeamDefaultsNoteExplainer />
                    ) : (
                        <Flex
                            gap="8px"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Text
                                    fontVariant="subtitle1"
                                    color={colorsPalettes.carbon["500"]}
                                    data-automation="sales-intelligence-extension-banner-team-defaults"
                                >
                                    {translate("si.management.extension.banner.team_defaults")}
                                </Text>
                                <Text
                                    fontVariant="body1"
                                    color={colorsPalettes.carbon["400"]}
                                    data-automation="sales-intelligence-extension-banner-individual-adjust"
                                >
                                    {translate("si.management.extension.banner.individual_adjust")}
                                </Text>
                            </Box>
                            <Button
                                onClick={() => {
                                    extensionManagementTrackingService.trackBannerLearnMore();
                                    setNoteExpanded(true);
                                }}
                                data-automation="sales-intelligence-extension-team-defaults-learn-more"
                            >
                                {translate("si.common.learn_more")}
                            </Button>
                        </Flex>
                    )
                ) : (
                    <ExtensionTeamDefaultsNoteExplainer />
                )}
            </AlertBanner.Description>
        </AlertBanner>
    );
};
